import React, {useState, useEffect} from 'react'
/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react'

import {connect} from 'react-redux'

import useMediaQuery from '@mui/material/useMediaQuery'

import {updateTimeFilter, initiateSearch, clearFilter} from '../../actions'

import TextField from '@mui/material/TextField'
import ChevronUpIcon from '@mui/icons-material/KeyboardArrowUp'
import ChevronDownIcon from '@mui/icons-material/KeyboardArrowDown'
import IconButton from '@mui/material/IconButton'


import FilterHeader from './FilterHeader'
import FilterFooter from './FilterFooter'


const cssStyles = ({padding, isSmallScreen}) => ({
  root: css`
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
  `,
  body: css`
    padding: ${padding ? padding : '20px 30px'};
  `,
  timeContainer: css`
    display: flex;
    max-width: 300px;
  `,
  text: css`
    margin-bottom: 5px;
    color: #9d9d9d;
  `,
  controlsContainer: css`
    display: flex;
    gap: 5px;
    width: 100%;
    justify-content: center;
    align-items: center;
  `,
  control: css`
    display: flex;
    justify-content: center;
    flex-direction: column;
    align-items: center;
  `,
  iconButton: css`
    border-radius: 0;
    width: 70px;
    padding: 5px 0px;
  `,
  field: css`
    width: 70px;
    height: 70px;

    & .MuiInputBase-root {
      width: 70px;
      height: 70px;
      text-align: center;
    }

    input {
      text-align: center;
      font-size: 20px;
    }
  `,
  colon: css`
    display: flex;
    align-items: center;
    margin-left: 5px;
    margin-right: 5px;
    font-size: 2rem;
  `,
  caption: css`
    margin-top: 15px;
    text-align: center;
  `,
  footerContainer: css`
    width: 100%;
    bottom: 0;
    background-color: #fff;
    z-index: 50
  `
})




const hoursRegex = /^(([01][\d]?)|([2][0-3])|[\d])$/
const minutesRegex = /^(([0-5][\d]?)|([\d]))$/



const TimeFilter = props => {

  const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('sm'))

  const {
    time, 
    isActive, 
    updateTimeFilter, 
    initiateSearch, 
    clearFilter,
    hideHeader,
    hideFooter,
    padding,
    callback,
    close
  } = props

    
  const styles = cssStyles({padding, isSmallScreen})


  const [hours, setHours] = useState(time.getHours() % 12 > 0 ? time.getHours() : '12')

  const [minutes, setMinutes] = useState(('0' + time.getMinutes()).slice(-2))

  const [ampm, setAmpm] = useState(time.getHours() >= 12 ? 'PM' : 'AM')



  useEffect(() => {
    if (callback) {
      const date = new Date()

      if (ampm === 'PM' && hours < 12) {
        date.setHours(hours + 12, minutes, 0, 0)
      } else if (ampm === 'AM' && hours > 11) {
        date.setHours(hours - 12, minutes, 0, 0)
      } else {
        date.setHours(hours, minutes, 0, 0)
      }


      if (time.getHours() !== date.getHours() || time.getMinutes() !== date.getMinutes()) {
        callback(date)
      }
    }
  }, [hours, minutes, ampm, callback, time])



  // Hour changes
  // --------------------------------------------------
  function handleHourChange(e) {
    const value = e.target.value

    const isValid = hoursRegex.test(value)
    
    if (isValid) {
      const integer = parseInt(value)

      if (integer > 12) {
        setAmpm('PM')
      }

      setHours(value)
    }
  }



  function incrementHours() {
    const newHour = parseInt(hours) + 1

    if (newHour === 24) {
      setHours('0')
      setAmpm('AM')
    } else {
      setHours(newHour)

      if (newHour > 12) {
        setAmpm('PM')
      }
    }
  }



  function decrementHours() {
    const newHour = parseInt(hours) - 1

    if (newHour === -1) {
      setHours('23')
      setAmpm('PM')
    } else {
      setHours(newHour)

      if (newHour > 12) {
        setAmpm('PM')
      }
    }
  }
  // --------------------------------------------------




  // Minute Changes
  // --------------------------------------------------
  function handleMinuteChange(e) {
    let value = e.target.value

    // If there is no value, then make the minutes the top of the hour
    if (!value) {
      value = '00'
    }

    
    // This handles instances where the user is using the keyboard to enter a time.
    // It ensures that 055 becomes 55 and 165 becomes 15.
    if (value.length === 3) {
      if (parseInt(value[1]) > 5) {
        value = value[0] + value[2]
      } else {
        value = value.slice(1)
      }
    }

    const isValid = minutesRegex.test(value)

    if (isValid) {
      setMinutes(('0' + value).slice(-2))
    }
  }



  function incrementMinutes() {
    const newMinute = parseInt(minutes) + 1

    if (newMinute === 60) {
      setMinutes('00')
    } else {
      setMinutes(('0' + newMinute).slice(-2)) 
    }
  }



  function decrementMinutes() {
    const newMinute = parseInt(minutes) - 1

    if (newMinute === -1) {
      setMinutes('59')
    } else {
      setMinutes(('0' + newMinute).slice(-2)) 
    }
  }
  // --------------------------------------------------






  // AM PM Change
  // --------------------------------------------------
  function toggleAmPm() {
    if (hours > 12) {
      if (ampm === 'PM') {
        setHours(hours - 12)
        setAmpm('AM')
      } else {
        setAmpm('PM')
      }
    } else {
      ampm === 'AM' ? setAmpm('PM') : setAmpm('AM')
    }
  }



  function handleAmPmChange(e) {
    const lastChar = e.target.value.slice(-1).toLowerCase()

    if (lastChar === 'p') {
      setAmpm('PM')
    } else if (lastChar === 'a') {
      if (hours > 12) {
        setHours(hours - 12)
      }

      setAmpm('AM')
    }
  }
  // --------------------------------------------------



  function apply() {
    const date = new Date()


    if (ampm === 'PM' && hours < 12) {
      date.setHours(hours + 12, minutes, 0, 0)
    } else if (ampm === 'AM' && hours > 11) {
      date.setHours(hours - 12, minutes, 0, 0)
    } else {
      date.setHours(hours, minutes, 0, 0)
    }


    if (time.getHours() !== date.getHours() || time.getMinutes() !== date.getMinutes()) {
      updateTimeFilter(date)
      initiateSearch()
      close()
    }
  }




  function clear() {
    setHours('12')
    setMinutes('00')
    setAmpm('AM')

    if (isActive) {
      clearFilter('TIME')
      initiateSearch()
      close()
    }
  }




  return (
    <div css={styles.root}>
      {!hideHeader &&
        <FilterHeader 
          title='Select Time' 
          caption='Works with 12 & 24 hours clocks'
          clear={clear}
          close={close}
        />
      }

      <div css={styles.body}>
        <div css={styles.controlsContainer}>
          <div css={styles.control}>
            <IconButton css={styles.iconButton} onClick={incrementHours} size='large'>
              <ChevronUpIcon fontSize='23px' />
            </IconButton>


            <TextField
              value={hours}
              placeholder='H'
              css={styles.field}
              onChange={handleHourChange}
            />


            <IconButton css={styles.iconButton} onClick={decrementHours} size='large'>
              <ChevronDownIcon fontSize='23px' />
            </IconButton>
          </div>


          <div css={styles.colon}>:</div>

          <div css={styles.control}>
            <IconButton css={styles.iconButton} onClick={incrementMinutes} size='large'>
              <ChevronUpIcon fontSize='23px' />
            </IconButton>


            <TextField
              value={minutes}
              placeholder='MM'
              css={styles.field}
              onChange={handleMinuteChange}
            />



            <IconButton css={styles.iconButton} onClick={decrementMinutes} size='large'>
              <ChevronDownIcon fontSize='23px' />
            </IconButton>
          </div>

          <div css={styles.control}>
            <IconButton css={styles.iconButton} onClick={toggleAmPm} size='large'>
              <ChevronUpIcon fontSize='23px' />
            </IconButton>


            <TextField 
              value={ampm}
              placeholder='PM' 
              css={styles.field}
              onChange={handleAmPmChange}
            />



            <IconButton css={styles.iconButton} onClick={toggleAmPm} size='large'>
              <ChevronDownIcon fontSize='23px' />
            </IconButton>
          </div>
        </div>


        <p css={styles.caption}>
          Starting at or after <strong>{`${hours}:${minutes} ${ampm}`}</strong>
        </p>
      </div>

      <div css={styles.footerContainer}>
        {!hideFooter && <FilterFooter onClick={apply} />}
      </div>
    </div>
  )
}


const mapStateToProps = state => {
  return {
    isActive: state.search.filters.time.isTimeActive,
    time: state.search.filters.time.start
  }
}


const actions = {updateTimeFilter, initiateSearch, clearFilter}


export default connect(mapStateToProps, actions)(TimeFilter)


